import { HttpBackend, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { SwPush } from '@angular/service-worker';
import { firstValueFrom, from, timeout } from 'rxjs';
import { environment } from '../../environments/environment';
import { UserType } from './enums';
import { ChatSenderType } from '../models/chat';
import { ChatService } from './chat.service';
import { MobileDashboardService } from './mobile-dashboard.service';

// One instance per chat panel; the browser itself retains the shared subscription.
@Injectable()
export class ChatPushService implements OnDestroy {
  enabled = false;
  busy = false;
  notice = '';
  showPrompt = false;
  copyNotice = '';
  readonly dashboard = inject(MobileDashboardService);
  private incidentId = 0;
  private participantType = UserType.unknown;
  private version = 0;
  private http: HttpClient;
  private resume = () => {
    if (this.permission !== 'granted') this.enabled = false;
    if (!this.enabled) void this.register();
  };

  constructor(private push: SwPush, backend: HttpBackend, private chat: ChatService) {
    // These public CCS requests must not depend on a saved callcentre login/token refresh.
    this.http = new HttpClient(backend);
    window.addEventListener('online', this.resume);
    window.addEventListener('focus', this.resume);
  }

  get configured() { return this.push.isEnabled && !!environment.vapidPublicKey; }
  get needsInstallation() { return this.configured && window.isSecureContext && this.dashboard.isIOS && !this.dashboard.standalone; }
  get supported() {
    return !this.needsInstallation && this.configured && window.isSecureContext && 'Notification' in window && 'PushManager' in window;
  }
  get dashboardLink() {
    const route = this.participantType === UserType.operator ? 'report-new-breakdown' : 'action';
    return `${window.location.origin}/${route}/${this.incidentId}`;
  }

  async copyDashboardLink() {
    try {
      await navigator.clipboard.writeText(this.dashboardLink);
      this.copyNotice = 'Link copied. Open Truck Breakdown from your Home Screen, then choose Open another breakdown.';
    } catch { this.copyNotice = 'Press and hold the link above to copy it.'; }
  }
  get permission(): NotificationPermission {
    return typeof Notification === 'undefined' ? 'denied' : Notification.permission;
  }

  setIncident(incidentId: number, participantType: UserType) {
    if (this.incidentId === incidentId && this.participantType === participantType) return;
    this.version++;
    this.incidentId = incidentId;
    this.participantType = participantType;
    this.enabled = this.busy = false;
    this.notice = '';
    this.copyNotice = '';
    this.showPrompt = incidentId > 0 && (this.needsInstallation || (this.supported && this.permission === 'default'));
    void this.register();
  }

  register(askPermission = false): Promise<void> {
    if (askPermission) this.showPrompt = false;
    if (!this.supported || !Number.isSafeInteger(this.incidentId) || this.incidentId <= 0 ||
        ![UserType.operator, UserType.serviceProviderOperator].includes(this.participantType) ||
        this.busy || this.enabled) return Promise.resolve();
    if (this.permission === 'denied' || (this.permission === 'default' && !askPermission)) return Promise.resolve();

    const version = this.version;
    const incidentId = this.incidentId, participantType = this.participantType;
    this.busy = true;
    this.notice = '';
    const registration = this.subscribe(version, incidentId, participantType);
    this.chat.registerSenderPushSubscription(incidentId,
      participantType === UserType.operator ? ChatSenderType.Fleet : ChatSenderType.ServiceProvider, registration);
    return registration.then(() => {});
  }

  private async subscribe(version: number, incidentId: number, participantType: UserType): Promise<number | undefined> {
    try {
      // Request permission directly during the button tap, before waiting for the worker.
      if (this.permission === 'default') {
        const permission = await Notification.requestPermission();
        if (version !== this.version) return;
        if (permission !== 'granted') {
          if (permission === 'default') this.notice = 'Notifications were not enabled. You can try again.';
          return;
        }
      }
      this.showPrompt = false;
      const existing = await firstValueFrom(this.push.subscription.pipe(timeout(15000)));
      if (version !== this.version) return;
      const subscription = existing ?? await firstValueFrom(from(this.push.requestSubscription({
        serverPublicKey: environment.vapidPublicKey
      })).pipe(timeout(15000)));
      if (version !== this.version) return;

      // Reuse the CCS host already configured for the incident WebSocket connection.
      const url = environment.wsUrl.replace(/^ws/, 'http').replace(/\/ws\/connect\/?$/, '/push');
      const result = await firstValueFrom(this.http.put<{ pushSubscriptionId: number }>(`${url}/incident/${incidentId}/${participantType}/subscription`,
        subscription.toJSON()).pipe(timeout(15000)));
      if (version === this.version) this.enabled = true;
      return result.pushSubscriptionId;
    } catch (error) {
      if (version !== this.version) return;
      this.notice = error instanceof HttpErrorResponse && error.status === 403
        ? 'Chat notifications are not allowed for this incident.'
        : 'Could not enable chat notifications. Please retry when connected.';
      return undefined;
    } finally {
      if (version === this.version) this.busy = false;
    }
  }

  ngOnDestroy() {
    this.version++;
    this.incidentId = 0;
    window.removeEventListener('online', this.resume);
    window.removeEventListener('focus', this.resume);
    // Leaving a dashboard must retain permission and its incident subscription.
  }
}
